import TeamMember from './TeamMember';
import TeamInvite from './TeamInvite';
import {TeamMemberRole} from './TeamMember';
import {sortBy} from '../util/array';
import {mediaTypeLabels} from 'bigdatr-theme';
import {SupportedCountry} from '../util/SupportedCountry';

/** AU advertising compliance codes as returned on team `Compliance` product (wire shape). */
export const TEAM_AUSTRALIA_COMPLIANCE_CODE_KEYS = [
    'AANA_CODE_OF_ETHICS',
    'AANA_FOOD_AND_BEVERAGES_ADVERTISING_CODE',
    'AANA_CHILDRENS_ADVERTISING_CODE',
    'AANA_ENVIRONMENTAL_CLAIMS_CODE',
    'AANA_WAGERING_ADVERTISING_CODE',
    'FCAI_CODE',
    'TGA_CODE'
] as const;

export type AustraliaComplianceCodeKey = (typeof TEAM_AUSTRALIA_COMPLIANCE_CODE_KEYS)[number];

export type AustraliaCompliancePermissions = Partial<
    Record<AustraliaComplianceCodeKey, string[] | null>
>;

/** Compliance Explore dropdown labels (aligned with {@link TEAM_AUSTRALIA_COMPLIANCE_CODE_KEYS}). */
export type ComplianceCodeName =
    | 'AANA Code of Ethics'
    | 'AANA Food & Beverages Advertising Code'
    | "AANA Children's Advertising Code"
    | 'AANA Environmental Claims Code'
    | 'AANA Wagering Advertising Code'
    | 'FCAI Code'
    | 'Therapeutic Goods Administration Code';

export const COMPLIANCE_OPTIONS: ComplianceCodeName[] = [
    'AANA Code of Ethics',
    'AANA Food & Beverages Advertising Code',
    "AANA Children's Advertising Code",
    'AANA Environmental Claims Code',
    'AANA Wagering Advertising Code',
    'FCAI Code',
    'Therapeutic Goods Administration Code'
];

const COMPLIANCE_CODE_ID_BY_LIST: Record<ComplianceCodeName, AustraliaComplianceCodeKey> = {
    'AANA Code of Ethics': 'AANA_CODE_OF_ETHICS',
    'AANA Food & Beverages Advertising Code': 'AANA_FOOD_AND_BEVERAGES_ADVERTISING_CODE',
    "AANA Children's Advertising Code": 'AANA_CHILDRENS_ADVERTISING_CODE',
    'AANA Environmental Claims Code': 'AANA_ENVIRONMENTAL_CLAIMS_CODE',
    'AANA Wagering Advertising Code': 'AANA_WAGERING_ADVERTISING_CODE',
    'FCAI Code': 'FCAI_CODE',
    'Therapeutic Goods Administration Code': 'TGA_CODE'
};

export function complianceNameToCodeId(name: ComplianceCodeName): AustraliaComplianceCodeKey {
    return COMPLIANCE_CODE_ID_BY_LIST[name];
}

function complianceCodeIdToName(key: AustraliaComplianceCodeKey): ComplianceCodeName | undefined {
    return COMPLIANCE_OPTIONS.find((label) => complianceNameToCodeId(label) === key);
}

export type ProductType =
    | 'TeamAdmin'
    | 'MediaValue'
    | 'Termatico'
    | 'AdvertisingCreative'
    | 'DataEntry'
    | 'Compliance';

export const CUSTOMER_PRODUCT_TYPES = ['AdvertisingCreative', 'MediaValue', 'Compliance'] as const;

export type CustomerProductType = (typeof CUSTOMER_PRODUCT_TYPES)[number];

function isCustomerProductType(product: ProductType): product is CustomerProductType {
    return CUSTOMER_PRODUCT_TYPES.includes(product as CustomerProductType);
}

function trialingProductHasIndustryAccess(product: Product): boolean {
    return (product.industryIds || []).length > 0;
}

function isTrialExpired(product: Product): boolean {
    const trialEnd = product.metadata?.trialEnd;
    if (trialEnd == null || trialEnd === '') return false;
    return Date.now() >= new Date(trialEnd).getTime();
}

function hasEffectiveCustomerProductAccess(product: Product): boolean {
    if (!isCustomerProductType(product.product) || product.status === INACTIVE) return false;
    if (product.status !== TRIALING) return true;
    if (isTrialExpired(product)) return false;

    switch (product.product) {
        case 'AdvertisingCreative':
            return product.country === SupportedCountry.AU
                ? trialingProductHasIndustryAccess(product)
                : true;
        case 'MediaValue':
            return trialingProductHasIndustryAccess(product);
        case 'Compliance':
            return true;
    }
}

export type Product = {
    product: ProductType;
    industryIds?: string[] | null;
    industries?: string[] | null;
    mediaTypeIds?: string[] | null;
    mediaTypes?: string[] | null;
    status: TeamProductStatus;
    metadata?: {trialEnd?: string} | null;
    country: SupportedCountry;
    /** Set when `product === 'Compliance'` (AU). Mirrors team GraphQL `compliance`. */
    compliance?: {AU?: AustraliaCompliancePermissions | null} | null;
};

export enum TeamProductStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    TRIALING = 'TRIALING'
}

const {INACTIVE, TRIALING} = TeamProductStatus;

export type TeamInput = {
    id: string;
    name: string;
    status?: string | null;
    members?: Array<TeamMember | null> | null;
    invites?: Array<TeamInvite | null> | null;
    products?: Array<Product | null> | null;
    trialExpired?: boolean | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    hasTrialTeamName?: boolean | null;
    downloadCount?: number | null;
    requestedNzTrial?: boolean | null;
};

export type MediaTypeAccessOption = {
    type?: string | null;
    id?: string | null;
    canonicalName?: string | null;
    hasAccess?: boolean | null;
};

export default class Team {
    id: string;
    name: string;
    status: string | null;
    members: Array<TeamMember>;
    invites: Array<TeamInvite>;
    products: Array<Product>;
    productMap: {
        [C in SupportedCountry]?: {
            [P in ProductType]?: Product;
        };
    };
    trialExpired: boolean | null;
    createdAt: string | null;
    updatedAt: string | null;
    hasTrialTeamName: boolean | null;
    downloadCount: number | null;
    requestedNzTrial: boolean | null;

    constructor(input: TeamInput) {
        this.id = input.id;
        this.name = input.name;
        this.status = input.status ?? null;
        this.trialExpired = input.trialExpired ?? null;
        this.createdAt = input.createdAt ?? null;
        this.updatedAt = input.updatedAt ?? null;
        this.hasTrialTeamName = input.hasTrialTeamName ?? null;
        this.downloadCount = input.downloadCount ?? null;
        this.requestedNzTrial = input.requestedNzTrial ?? null;
        this.members = (input.members || []).map((m: any) => new TeamMember(m));
        this.invites = (input.invites || []).map((m: any) => new TeamInvite(m));
        this.products = input.products?.filter((product): product is Product => !!product) || [];
        this.productMap = this.products.reduce((map, product) => {
            map[product.country] ||= {};
            // need to do assign products to a country like this because otherwise typescript cries
            const countryMap = map[product.country];
            if (countryMap) countryMap[product.product] = product;
            return map;
        }, {} as typeof this.productMap);
    }

    /**
     * Helper to check whether you can edit (change role, remove) a member of a team
     *
     * This is useful when complying with the rule of always having one owner on a team
     */
    canEditMember(member: TeamMember) {
        return this.canEditMemberRole(member, 'OWNER') || this.canEditMemberRole(member, 'MEMBER');
    }

    canEditMemberRole(member: TeamMember, role: TeamMemberRole) {
        if (this.id !== member.teamId) return false;

        switch (role) {
            case 'OWNER': {
                const hasMultipleOwners =
                    this.members.filter((member) => member.role === 'OWNER').length > 1;
                return member.role === 'OWNER' && hasMultipleOwners;
            }
            case 'MEMBER':
                return member.role === 'MEMBER';
            default:
                return false;
        }
    }

    //
    // Products

    get australianProducts() {
        return this.productMap.Australia ?? {};
    }

    get newZealandProducts() {
        return this.productMap['New Zealand'] ?? {};
    }

    get hasNoPermissions() {
        return this.products.length === 0 || this.products.every((p) => p.status === INACTIVE);
    }

    get hasMoreThanOneCountry() {
        return this.countriesWithAccess.length > 1;
    }

    get isAdminOnlyTeam() {
        return (
            (this.hasTeamAdmin || this.hasDataEntry || this.hasTermatico) &&
            !this.hasCustomerProductAccess
        );
    }

    get hasTeamAdmin() {
        return Boolean(this.productMap.Australia?.TeamAdmin);
    }

    get hasTermatico() {
        return Boolean(this.productMap.Australia?.Termatico);
    }

    get hasAusCreative() {
        const product = this.australianProducts.AdvertisingCreative;
        if (!product || product.status === INACTIVE) return false;
        if (product.status === TRIALING) return (product.industryIds || []).length > 0;
        return true;
    }

    get hasNzCreative() {
        const product = this.newZealandProducts.AdvertisingCreative;
        if (!product || product.status === INACTIVE) return false;
        return true;
    }

    get hasAusMediaValue() {
        const product = this.australianProducts.MediaValue;
        if (!product || product.status === INACTIVE) return false;
        if (product.status === TRIALING) return (product.industryIds || []).length > 0;
        return true;
    }

    get hasNzMediaValue() {
        const product = this.newZealandProducts.MediaValue;
        if (!product || product.status === INACTIVE) return false;
        if (product.status === TRIALING) return (product.industryIds || []).length > 0;
        return true;
    }

    get hasCompliance() {
        const product = this.australianProducts.Compliance;
        if (!product || product.status === INACTIVE) return false;
        const hasIndustriesInAnyCode = Object.values(product.compliance?.AU || {}).some(
            (industries) => !!industries?.length
        );

        return hasIndustriesInAnyCode;
    }

    get hasOnlyCompliance() {
        return this.hasCompliance && !this.hasAusCreative && !this.hasAusMediaValue;
    }

    get hasDataEntry() {
        return Boolean(this.productMap.Australia?.DataEntry);
    }

    get hasCustomerProductAccess() {
        return this.customerProductsWithAccess.length > 0;
    }

    get customerProductsWithAccess() {
        return this.products.filter(hasEffectiveCustomerProductAccess);
    }

    get countriesWithAccess() {
        return [...new Set(this.customerProductsWithAccess.map((p) => p.country))];
    }

    get trialEndDate() {
        const longestTrialEndDate = sortBy(
            this.products,
            (pp) => {
                if (pp?.metadata?.trialEnd) return new Date(pp.metadata.trialEnd).getTime();
                return '';
            },
            true
        )[0];

        return longestTrialEndDate?.metadata?.trialEnd;
    }

    get isTrialNotExpired() {
        return this.trialEndDate
            ? new Date().getTime() < new Date(this.trialEndDate).getTime()
            : false;
    }

    get mediaValueIndustryNames() {
        return this.productMap.Australia?.MediaValue?.industries ?? [];
    }

    get isAnyCustomerProductOnTrial() {
        const customerProducts = this.products.filter((p) => isCustomerProductType(p.product));
        return customerProducts.some((p) => {
            return p.status === TRIALING;
        });
    }

    advertisingCreativeIndustryNames(country: SupportedCountry) {
        return this.productMap[country]?.AdvertisingCreative?.industries ?? [];
    }

    mediaValueIsAllowedIndustry(industryId: string) {
        if (!this.productMap.Australia?.MediaValue) return false;
        const industries = this.productMap.Australia?.MediaValue?.industryIds || [];
        return industries.length === 0 || industries.includes(industryId) || industryId === 'All';
    }

    getMediaTypeAccess({
        option,
        productKey,
        country
    }: {
        option: MediaTypeAccessOption;
        productKey: CustomerProductType;
        country: SupportedCountry;
    }): boolean {
        let isMediaTypeLocked = option.hasAccess === false;

        if (option.type === 'mediaType') {
            // Media type gating is product-specific; determine if this option should be locked.
            const product = this.productMap[country]?.[productKey];
            const mediaTypeIds = product?.mediaTypeIds ?? [];
            const mediaTypes = product?.mediaTypes ?? [];

            // Only gate when the option has an id.
            // If gating isn't configured, this should not be treated as locked.
            if (!option.id) {
                return false;
            }

            const hasCanonicalNameAccess = option.canonicalName
                ? mediaTypes.includes(option.canonicalName)
                : false;

            isMediaTypeLocked = !hasCanonicalNameAccess && !mediaTypeIds.includes(option.id);
        }

        return isMediaTypeLocked;
    }

    isMediaTypeAllowed({
        mediaTypeId,
        productKey,
        country
    }: {
        mediaTypeId: string;
        productKey: CustomerProductType;
        country: SupportedCountry;
    }): boolean {
        const mediaTypeIdsFromPermissions =
            this.productMap[country]?.[productKey]?.mediaTypeIds ?? [];
        return mediaTypeIdsFromPermissions.includes(mediaTypeId);
    }

    getMediaTypeNamesAndSynonyms(input: {
        product: CustomerProductType;
        country: SupportedCountry;
    }): string[] {
        const mediaTypesFromProduct = this.productMap[input.country]?.[input.product]?.mediaTypes;
        if (!mediaTypesFromProduct) return [];

        const mediaTypeNamesAndSynonymsFromProduct = new Set<string>();

        mediaTypesFromProduct.forEach((canonicalName) => {
            mediaTypeNamesAndSynonymsFromProduct.add(canonicalName);
            const synonym = mediaTypeLabels[canonicalName];
            if (synonym) mediaTypeNamesAndSynonymsFromProduct.add(synonym);
        });

        return Array.from(mediaTypeNamesAndSynonymsFromProduct);
    }

    //
    // Compliance Explore (AU)

    /** AU Compliance subscription row on this team. */
    static isAuComplianceProductUsable(product: Product | undefined): boolean {
        if (
            !product ||
            product.product !== 'Compliance' ||
            product.country !== SupportedCountry.AU
        ) {
            return false;
        }
        if (product.status === TeamProductStatus.INACTIVE) {
            return false;
        }
        if (product.status === TeamProductStatus.ACTIVE) {
            return true;
        }
        if (product.status === TeamProductStatus.TRIALING) {
            return !isTrialExpired(product);
        }
        return false;
    }

    /** LaunchDarkly gate for Compliance Explore (nav + routes + page). */
    static complianceExploreFeatureFlagsEnabled(flags: {
        productionisedCompliance?: boolean;
    }): boolean {
        return Boolean(flags.productionisedCompliance);
    }

    /**
     * Compliance Explore ad-code choices from this team's AU Compliance product.
     * A code is included only when the admin has saved at least one industry for that code.
     */
    permittedComplianceLists(): ComplianceCodeName[] {
        const complianceProduct = this.australianProducts.Compliance;
        if (!complianceProduct || !Team.isAuComplianceProductUsable(complianceProduct)) {
            return [];
        }

        const au = complianceProduct.compliance?.AU;
        if (!au) {
            return [];
        }

        const permitted: ComplianceCodeName[] = [];
        for (const key of TEAM_AUSTRALIA_COMPLIANCE_CODE_KEYS) {
            const industries = au[key];
            if (Array.isArray(industries) && industries.length > 0) {
                const label = complianceCodeIdToName(key);
                if (label) permitted.push(label);
            }
        }
        return permitted;
    }

    /** True when productionised Compliance is on and this team has at least one configured AU ad code. */
    canAccessComplianceExplore(flags: {productionisedCompliance?: boolean}): boolean {
        if (!Team.complianceExploreFeatureFlagsEnabled(flags)) {
            return false;
        }
        return this.permittedComplianceLists().length > 0;
    }

    /** True when legacy (mock) Compliance Explore should be available. */
    static canAccessLegacyComplianceExplore(flags: {
        advertisingCompliance?: boolean;
        productionisedCompliance?: boolean;
    }): boolean {
        return Boolean(flags.advertisingCompliance) && !flags.productionisedCompliance;
    }
}
